import { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { BookingLink } from '@/components/BookingLink';
import { Card } from '@/components/Card';
import { Chip, ChipRow } from '@/components/ChipRow';
import { ProvenanceNote } from '@/components/Provenance';
import { api } from '@/lib/api';
import type { Service } from '@shared/types';

export function ComparePage() {
  const [params] = useSearchParams();
  const ids = (params.get('ids') ?? '').split(',').filter(Boolean);
  const [services, setServices] = useState<Service[]>([]);
  const [notice, setNotice] = useState<string | null>(null);

  useEffect(() => {
    if (ids.length < 2) return;
    api.compare(ids).then((data) => {
      setServices(data.services);
      setNotice(data.notice);
    });
  }, [params]);

  if (ids.length < 2) {
    return <p className="text-sm text-muted">Pick at least two services from a trip plan to compare.</p>;
  }

  return (
    <div className="space-y-4">
      <Card>
        <h2 className="font-display text-[30px] leading-none text-navy">Service compare</h2>
        <p className="mt-2 text-sm text-muted">{notice}</p>
        <ChipRow className="mt-3">
          {services.map((service) => (
            <Chip key={service.id} tone="navy">
              {service.airportIata} {service.name}
            </Chip>
          ))}
        </ChipRow>
      </Card>
      <div className="grid grid-cols-1 gap-3">
        {services.map((service) => (
          <Card key={service.id}>
            <ChipRow>
              <Chip>{service.kind}</Chip>
              <Chip>{service.airportIata}</Chip>
            </ChipRow>
            <h3 className="mt-2 font-display text-2xl text-navy">{service.name}</h3>
            <dl className="mt-3 space-y-2 text-sm">
              <div>
                <dt className="text-[11px] font-semibold uppercase tracking-wide text-muted">Where</dt>
                <dd>{service.terminal || service.locationNotes || 'See notes'}</dd>
              </div>
              <div>
                <dt className="text-[11px] font-semibold uppercase tracking-wide text-muted">Eligibility</dt>
                <dd>{service.eligibilityNotes}</dd>
              </div>
              <div>
                <dt className="text-[11px] font-semibold uppercase tracking-wide text-muted">Useful time</dt>
                <dd>{service.minUsefulMinutes ? `${service.minUsefulMinutes}+ minutes` : 'Not a timed hold'}</dd>
              </div>
              <div>
                <dt className="text-[11px] font-semibold uppercase tracking-wide text-muted">Seeded cost</dt>
                <dd>
                  {service.prices.length
                    ? service.prices.map((p) => p.amountNote || p.label).join(' · ')
                    : 'No price in seed'}
                </dd>
              </div>
            </dl>
            <BookingLink href={service.bookingUrl} kind={service.kind} />
            <ProvenanceNote className="mt-2" provenance={service.provenance} />
          </Card>
        ))}
      </div>
    </div>
  );
}
